/**
 * ExternalRepliesPanel — Iter Feb 2026 R47 · Phase 2.
 * Shows replies captured from the workspace's connected IMAP mailbox.
 * Each row surfaces whether the reply was matched back to an original
 * send (Message-ID lookup) and, if so, the target entity.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Mailbox, RefreshCw, ChevronDown, ChevronRight, LinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString();
};

function ReplyRow({ row, ws }) {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(null);
  const toggle = async () => {
    if (!open && !full) {
      try {
        const { data } = await api.get(
          `/workspaces/${ws}/mail/external-replies/${encodeURIComponent(row.message_id || row.uid)}`);
        setFull(data);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to fetch reply")); return; }
    }
    setOpen(!open);
  };
  return (
    <div className="border-b border-border py-2" data-testid={`reply-row-${row.uid}`}>
      <button onClick={toggle} className="w-full flex items-start gap-2 text-left">
        {open ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold truncate">{row.from || "(no sender)"}</span>
            {row.matched ? (
              <span className="chip text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300">
                <LinkIcon className="w-2.5 h-2.5 inline" /> matched
              </span>
            ) : (
              <span className="chip text-[10px] bg-neutral-100 text-neutral-600">unlinked</span>
            )}
            {row.entity_type && (
              <span className="chip text-[10px] uppercase bg-sky-100 text-sky-700 border-sky-300">
                {row.entity_type}
              </span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap">{relTime(row.date)}</span>
          </div>
          <div className="text-xs truncate">{row.subject || "(no subject)"}</div>
          <div className="text-[11px] text-muted-foreground line-clamp-1">{(row.text_preview || "").slice(0, 220)}</div>
        </div>
      </button>
      {open && full && (
        <div className="mt-2 border-l-2 border-neutral-300 pl-3 pr-1 py-2 bg-neutral-50">
          {full.html ? (
            <div className="max-w-full overflow-x-auto text-sm" dangerouslySetInnerHTML={{ __html: full.html }} />
          ) : (
            <pre className="text-xs whitespace-pre-wrap font-sans">{full.text_preview}</pre>
          )}
          <div className="text-[10px] text-muted-foreground mt-2 border-t pt-1">
            UID {full.uid} · Message-ID: <code>{full.message_id}</code>
            {full.original_send_id && <> · In reply to send <code>{full.original_send_id}</code></>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExternalRepliesPanel({ entityType, entityId }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [matchedOnly, setMatchedOnly] = useState(false);

  const load = () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/mail/external-replies`,
             { params: { matched_only: matchedOnly } })
      .then(r => {
        let list = r.data.rows || [];
        if (entityType && entityId) list = list.filter(x => x.entity_type === entityType && x.entity_id === entityId);
        setRows(list);
      })
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load replies")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [activeWorkspaceId, matchedOnly, entityType, entityId]); // eslint-disable-line

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/settings/imap-mailbox/poll`);
      toast.success(`Scanned ${data.scanned || 0} · captured ${data.recorded || 0} new`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Poll failed")); }
    finally { setPolling(false); }
  };

  return (
    <div className="border border-border bg-background p-4" data-testid="external-replies-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mailbox className="w-4 h-4 text-blue-600" />
          <div>
            <div className="text-sm font-semibold">External replies (IMAP)</div>
            <div className="text-[11px] text-muted-foreground">Replies pulled from your connected mailbox and threaded back to the entity.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[11px] cursor-pointer">
            <input type="checkbox" checked={matchedOnly} onChange={(e) => setMatchedOnly(e.target.checked)} data-testid="reply-matched-only" />
            Matched only
          </label>
          <Button size="sm" variant="outline" onClick={pollNow} disabled={polling} className="rounded-none h-7" data-testid="reply-poll-now">
            {polling ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Polling…</> : <><RefreshCw className="w-3 h-3 mr-1" /> Poll now</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-6 text-center">Loading replies…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center border-dashed border p-4">
          No replies captured yet. Configure IMAP under Settings → Integrations → IMAP inbox first, then send an email via the Manual Composer.
        </div>
      ) : (
        <div>
          {rows.map((r, i) => <ReplyRow key={r.message_id || r.uid || i} row={r} ws={activeWorkspaceId} />)}
        </div>
      )}
    </div>
  );
}
